import React, { useState } from "react";
import { Link } from "react-router-dom";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import { useFirstAidAnalytics } from "../../../query/hooks/QueryHooksFirstAidBox";
import { DEFAULT_EXPIRY_WINDOW_DAYS, OHS_BASE_PATH } from "./ohsConstants";
import ExpiringSoon from "./ExpiringSoon";
import {
    ChartLegend,
    chartColors,
    EmptyState,
    LegendItem,
    LegendSwatch,
    MutedText,
    RankedBarLabel,
    RankedBarRow,
    RankedBarSegment,
    RankedBarTrack,
    RankedBarValue,
    SectionCard,
    SectionHeader,
    SectionTitle
} from "./Ohs.styles";

const MOST_NEEDED_LIMIT = 10;
// Tablefilter state - checkObj keys are uniqueId + first word of filterName (lower case) + value.
const DEFAULT_EXPIRY_FILTER = {
    arr: [String(DEFAULT_EXPIRY_WINDOW_DAYS)],
    checkObj: { [`ohsExpiryexpiring${DEFAULT_EXPIRY_WINDOW_DAYS}`]: true },
};
/**
 * Analytics - compliance by campus (worst first), first aider accuracy, most needed items, and items expiring soon.
 */
function Analytics() {
    const [expiryFilter, setExpiryFilter] = useState(DEFAULT_EXPIRY_FILTER);
    // The filter is single-select; clearing it (Reset) falls back to the default window.
    const expiryWindowDays = Number(expiryFilter.arr[0]) || DEFAULT_EXPIRY_WINDOW_DAYS;
    const { data: analytics, isLoading } = useFirstAidAnalytics(expiryWindowDays);

    if (isLoading || !analytics) {
        return <SkeletonTicketList />;
    }

    const mostNeededItems = analytics.mostNeededItems.slice(0, MOST_NEEDED_LIMIT);
    const highestBoxCount = Math.max(1, ...mostNeededItems.map((restockItem) => restockItem.boxCount));

    return (
        <>
            <SectionCard>
                <SectionHeader>
                    <div>
                        <SectionTitle>Compliance by Campus</SectionTitle>
                        <MutedText>Worst first - share of active boxes whose last check came back OK.</MutedText>
                    </div>
                </SectionHeader>
                <ChartLegend>
                    <LegendItem><LegendSwatch $color={chartColors.compliant} />Compliant</LegendItem>
                    <LegendItem><LegendSwatch $color={chartColors.nonCompliant} />Non-compliant</LegendItem>
                    <LegendItem><LegendSwatch $color={chartColors.track} />Not yet checked</LegendItem>
                </ChartLegend>
                {analytics.complianceByCampus.length === 0 ? (
                    <EmptyState>No active boxes yet.</EmptyState>
                ) : (
                    analytics.complianceByCampus.map((campus) => (
                        <RankedBarRow key={campus.campus_id}>
                            <RankedBarLabel title={campus.campus_name}>{campus.campus_name}</RankedBarLabel>
                            <RankedBarTrack role="img" aria-label={`${campus.campus_name}: ${campus.compliant} compliant, ${campus.nonCompliant} non-compliant, ${campus.notYetChecked} not yet checked`}>
                                {campus.compliant > 0 && (
                                    <RankedBarSegment $color={chartColors.compliant} $share={campus.compliant} title={`${campus.compliant} compliant`} />
                                )}
                                {campus.nonCompliant > 0 && (
                                    <RankedBarSegment $color={chartColors.nonCompliant} $share={campus.nonCompliant} title={`${campus.nonCompliant} non-compliant`} />
                                )}
                                {campus.notYetChecked > 0 && (
                                    <RankedBarSegment $color={chartColors.track} $share={campus.notYetChecked} title={`${campus.notYetChecked} not yet checked`} />
                                )}
                            </RankedBarTrack>
                            <RankedBarValue>
                                {campus.compliantPercent}% of {campus.totalBoxes} · {campus.notCheckedThisMonth} overdue
                            </RankedBarValue>
                        </RankedBarRow>
                    ))
                )}
            </SectionCard>

            <SectionCard>
                <SectionHeader>
                    <div>
                        <SectionTitle>First Aider Accuracy</SectionTitle>
                        <MutedText>
                            Least accurate first - each item on an OHS Rep Check, compared with the First Aider Check before it.
                            Items used via QR in between aren&apos;t counted.
                        </MutedText>
                    </div>
                    <Link to={`${OHS_BASE_PATH}/discrepancies`}>All discrepancies</Link>
                </SectionHeader>
                <ChartLegend>
                    <LegendItem><LegendSwatch $color={chartColors.compliant} />Matched the OHS Rep Check</LegendItem>
                    <LegendItem><LegendSwatch $color={chartColors.nonCompliant} />Discrepancy</LegendItem>
                </ChartLegend>
                {analytics.firstAiderAccuracy.length === 0 ? (
                    <EmptyState>No OHS Rep Checks to compare yet. Use Perform OHS Rep Check on a box to start.</EmptyState>
                ) : (
                    analytics.firstAiderAccuracy.map((firstAider) => {
                        const matchedItems = firstAider.itemsVerified - firstAider.discrepancies;
                        return (
                            <RankedBarRow key={firstAider.user_id}>
                                <RankedBarLabel title={firstAider.name}>{firstAider.name}</RankedBarLabel>
                                <RankedBarTrack role="img" aria-label={`${firstAider.name}: ${matchedItems} matched, ${firstAider.discrepancies} discrepancies`}>
                                    {matchedItems > 0 && (
                                        <RankedBarSegment $color={chartColors.compliant} $share={matchedItems} title={`${matchedItems} matched`} />
                                    )}
                                    {firstAider.discrepancies > 0 && (
                                        <RankedBarSegment $color={chartColors.nonCompliant} $share={firstAider.discrepancies} title={`${firstAider.discrepancies} discrepancies`} />
                                    )}
                                </RankedBarTrack>
                                <RankedBarValue>
                                    {firstAider.accuracyPercent}% of {firstAider.itemsVerified} items · {firstAider.overReportedUnits} units over-reported · {firstAider.boxesVerified} {firstAider.boxesVerified === 1 ? "box" : "boxes"}
                                </RankedBarValue>
                            </RankedBarRow>
                        );
                    })
                )}
            </SectionCard>

            <SectionCard>
                <SectionHeader>
                    <div>
                        <SectionTitle>Most Needed Items</SectionTitle>
                        <MutedText>Top {MOST_NEEDED_LIMIT} by number of boxes currently needing a restock.</MutedText>
                    </div>
                    <Link to={`${OHS_BASE_PATH}/restock`}>Full restock list</Link>
                </SectionHeader>
                {mostNeededItems.length === 0 ? (
                    <EmptyState>Nothing needs restocking right now.</EmptyState>
                ) : (
                    mostNeededItems.map((restockItem) => (
                        <RankedBarRow key={restockItem.box_item_id}>
                            <RankedBarLabel title={restockItem.item_name}>{restockItem.item_name}</RankedBarLabel>
                            <RankedBarTrack role="img" aria-label={`${restockItem.item_name}: ${restockItem.boxCount} boxes`}>
                                <RankedBarSegment $color={chartColors.single} $share={restockItem.boxCount} title={`${restockItem.boxCount} boxes · ${restockItem.totalUnitsToOrder} to order`} />
                                {restockItem.boxCount < highestBoxCount && (
                                    <RankedBarSegment $color={chartColors.track} $share={highestBoxCount - restockItem.boxCount} />
                                )}
                            </RankedBarTrack>
                            <RankedBarValue>
                                {restockItem.boxCount} {restockItem.boxCount === 1 ? "box" : "boxes"} · {restockItem.totalUnitsToOrder} to order
                            </RankedBarValue>
                        </RankedBarRow>
                    ))
                )}
            </SectionCard>

            <ExpiringSoon
                expiringSoon={analytics.expiringSoon}
                expiryFilter={expiryFilter}
                setExpiryFilter={setExpiryFilter}
            />
        </>
    );
}

export default Analytics;
